import { createServerFn } from "@tanstack/react-start";
import { and, eq, isNull, ne, or } from "drizzle-orm";
import { z } from "zod";
import { projectCitations, screeningDecisions, user } from "~/lib/db/schema";

// ============================================================================
// Schemas
// ============================================================================

const phaseEnum = z.enum(["title_abstract", "full_text"]);

const projectIdSchema = z.object({
  projectId: z.string(),
  phase: phaseEnum.default("title_abstract"),
});

const reviewerStatsSchema = z.object({
  projectId: z.string(),
  reviewerId: z.string().optional(), // If not provided, returns stats for all reviewers
  phase: phaseEnum.default("title_abstract"),
});

const kappaSchema = z.object({
  projectId: z.string(),
  reviewer1Id: z.string().optional(),
  reviewer2Id: z.string().optional(),
  phase: phaseEnum.default("title_abstract"),
});

// ============================================================================
// Types
// ============================================================================

export interface AgreementStats {
  total: number;
  pending: number;
  awaitingSecond: number;
  agreed: number;
  conflict: number;
  adjudicated: number;
  agreementRate: number; // agreed / (agreed + conflict + adjudicated)
}

export interface ReviewerStats {
  reviewerId: string;
  reviewerName: string;
  totalScreened: number;
  includeCount: number;
  excludeCount: number;
  maybeCount: number;
  averageTimeSeconds: number | null;
  agreementRate: number | null; // With other reviewers
}

export interface KappaResult {
  kappa: number;
  interpretation: string;
  observedAgreement: number;
  expectedAgreement: number;
  totalPairs: number;
  matrix: number[][]; // 3x3 confusion matrix [include, exclude, maybe]
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Interpret Cohen's Kappa value
 */
function interpretKappa(kappa: number): string {
  if (kappa < 0) return "Poor (less than chance)";
  if (kappa < 0.2) return "Slight";
  if (kappa < 0.4) return "Fair";
  if (kappa < 0.6) return "Moderate";
  if (kappa < 0.8) return "Substantial";
  return "Almost Perfect";
}

/**
 * Calculate Cohen's Kappa from a confusion matrix
 */
function calculateKappaFromMatrix(matrix: number[][]): {
  kappa: number;
  observedAgreement: number;
  expectedAgreement: number;
} {
  const n = matrix.reduce((sum, row) => sum + row.reduce((s, v) => s + v, 0), 0);

  if (n === 0) {
    return { kappa: 0, observedAgreement: 0, expectedAgreement: 0 };
  }

  // Observed agreement (diagonal sum / total)
  let observedAgreement = 0;
  for (let i = 0; i < matrix.length; i++) {
    observedAgreement += matrix[i][i];
  }
  const Po = observedAgreement / n;

  // Expected agreement by chance
  // Pe = sum(row_i_total * col_i_total) / n^2
  const rowTotals = matrix.map((row) => row.reduce((s, v) => s + v, 0));
  const colTotals = matrix[0].map((_, i) => matrix.reduce((s, row) => s + row[i], 0));

  let Pe = 0;
  for (let i = 0; i < matrix.length; i++) {
    Pe += (rowTotals[i] * colTotals[i]) / (n * n);
  }

  // Cohen's Kappa
  const kappa = Pe === 1 ? 1 : (Po - Pe) / (1 - Pe);

  return {
    kappa: isNaN(kappa) ? 0 : kappa,
    observedAgreement: Po,
    expectedAgreement: Pe,
  };
}

// ============================================================================
// Server Functions
// ============================================================================

/**
 * Calculate agreement statistics for a project
 */
export const $getAgreementStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      columns: {
        consensusStatus: true,
      },
    });

    let pending = 0;
    let awaitingSecond = 0;
    let agreed = 0;
    let conflict = 0;
    let adjudicated = 0;

    for (const c of citations) {
      switch (c.consensusStatus) {
        case "pending":
          pending++;
          break;
        case "awaiting_second":
          awaitingSecond++;
          break;
        case "agreed_include":
        case "agreed_exclude":
        case "agreed_maybe":
          agreed++;
          break;
        case "conflict":
          conflict++;
          break;
        case "adjudicated":
          adjudicated++;
          break;
      }
    }

    const completedPairs = agreed + conflict + adjudicated;
    const agreementRate = completedPairs > 0 ? agreed / completedPairs : 0;

    return {
      total: citations.length,
      pending,
      awaitingSecond,
      agreed,
      conflict,
      adjudicated,
      agreementRate,
    } satisfies AgreementStats;
  });

/**
 * Calculate Cohen's Kappa for inter-rater reliability
 */
export const $getCohensKappa = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => kappaSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    // Get all active decisions for this project filtered by phase
    const allDecisions = await db
      .select({
        projectCitationId: screeningDecisions.projectCitationId,
        reviewerId: screeningDecisions.reviewerId,
        decision: screeningDecisions.decision,
      })
      .from(screeningDecisions)
      .innerJoin(
        projectCitations,
        eq(screeningDecisions.projectCitationId, projectCitations.id),
      )
      .where(
        and(
          eq(projectCitations.projectId, data.projectId),
          eq(screeningDecisions.phase, data.phase),
          isNull(screeningDecisions.deletedAt),
        ),
      );

    // Group decisions by citation
    const decisionsByCitation = new Map<
      string,
      Array<{ reviewerId: string; decision: string }>
    >();
    for (const d of allDecisions) {
      if (!decisionsByCitation.has(d.projectCitationId)) {
        decisionsByCitation.set(d.projectCitationId, []);
      }
      decisionsByCitation.get(d.projectCitationId)!.push({
        reviewerId: d.reviewerId,
        decision: d.decision,
      });
    }

    // Filter to citations with exactly 2 decisions
    // If reviewer IDs specified, filter to just those
    const pairs: Array<{ d1: string; d2: string }> = [];

    for (const [, decisions] of decisionsByCitation) {
      if (decisions.length !== 2) continue;

      const [dec1, dec2] = decisions;

      // If specific reviewers requested, filter
      if (data.reviewer1Id && data.reviewer2Id) {
        const hasReviewer1 = decisions.some((d) => d.reviewerId === data.reviewer1Id);
        const hasReviewer2 = decisions.some((d) => d.reviewerId === data.reviewer2Id);
        if (!hasReviewer1 || !hasReviewer2) continue;

        // Order by specified reviewer IDs
        const r1Dec = decisions.find((d) => d.reviewerId === data.reviewer1Id)!;
        const r2Dec = decisions.find((d) => d.reviewerId === data.reviewer2Id)!;
        pairs.push({ d1: r1Dec.decision, d2: r2Dec.decision });
      } else {
        // Use natural order
        pairs.push({ d1: dec1.decision, d2: dec2.decision });
      }
    }

    if (pairs.length === 0) {
      return {
        kappa: 0,
        interpretation: "Insufficient data",
        observedAgreement: 0,
        expectedAgreement: 0,
        totalPairs: 0,
        matrix: [
          [0, 0, 0],
          [0, 0, 0],
          [0, 0, 0],
        ],
      } satisfies KappaResult;
    }

    // Build 3x3 confusion matrix
    // Rows: reviewer 1 (include=0, exclude=1, maybe=2)
    // Cols: reviewer 2 (include=0, exclude=1, maybe=2)
    const decisionIndex: Record<string, number> = {
      include: 0,
      exclude: 1,
      maybe: 2,
    };

    const matrix = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ];

    for (const pair of pairs) {
      const i = decisionIndex[pair.d1] ?? 2;
      const j = decisionIndex[pair.d2] ?? 2;
      matrix[i][j]++;
    }

    const { kappa, observedAgreement, expectedAgreement } =
      calculateKappaFromMatrix(matrix);

    return {
      kappa,
      interpretation: interpretKappa(kappa),
      observedAgreement,
      expectedAgreement,
      totalPairs: pairs.length,
      matrix,
    } satisfies KappaResult;
  });

/**
 * Get per-reviewer statistics
 */
export const $getReviewerStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => reviewerStatsSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    // Get all reviewers who have made decisions in this project
    const reviewerDecisions = await db
      .select({
        reviewerId: screeningDecisions.reviewerId,
        reviewerName: user.name,
        decision: screeningDecisions.decision,
        timeSpentSeconds: screeningDecisions.timeSpentSeconds,
        projectCitationId: screeningDecisions.projectCitationId,
      })
      .from(screeningDecisions)
      .innerJoin(user, eq(screeningDecisions.reviewerId, user.id))
      .innerJoin(
        projectCitations,
        eq(screeningDecisions.projectCitationId, projectCitations.id),
      )
      .where(
        and(
          eq(projectCitations.projectId, data.projectId),
          eq(screeningDecisions.phase, data.phase),
          isNull(screeningDecisions.deletedAt),
          data.reviewerId
            ? eq(screeningDecisions.reviewerId, data.reviewerId)
            : undefined,
        ),
      );

    // Group by reviewer
    const reviewerMap = new Map<
      string,
      {
        name: string;
        decisions: Array<{
          decision: string;
          timeSpentSeconds: number | null;
          citationId: string;
        }>;
      }
    >();

    for (const d of reviewerDecisions) {
      if (!reviewerMap.has(d.reviewerId)) {
        reviewerMap.set(d.reviewerId, {
          name: d.reviewerName,
          decisions: [],
        });
      }
      reviewerMap.get(d.reviewerId)!.decisions.push({
        decision: d.decision,
        timeSpentSeconds: d.timeSpentSeconds,
        citationId: d.projectCitationId,
      });
    }

    // Calculate stats for each reviewer
    const stats: ReviewerStats[] = [];

    for (const [reviewerId, data] of reviewerMap) {
      const { name, decisions } = data;

      let includeCount = 0;
      let excludeCount = 0;
      let maybeCount = 0;
      let totalTime = 0;
      let timeCount = 0;

      for (const d of decisions) {
        if (d.decision === "include") includeCount++;
        else if (d.decision === "exclude") excludeCount++;
        else maybeCount++;

        if (d.timeSpentSeconds != null) {
          totalTime += d.timeSpentSeconds;
          timeCount++;
        }
      }

      stats.push({
        reviewerId,
        reviewerName: name,
        totalScreened: decisions.length,
        includeCount,
        excludeCount,
        maybeCount,
        averageTimeSeconds: timeCount > 0 ? totalTime / timeCount : null,
        agreementRate: null, // Could calculate pairwise, but expensive
      });
    }

    // Sort by total screened descending
    stats.sort((a, b) => b.totalScreened - a.totalScreened);

    return stats;
  });

/**
 * Get overall project screening statistics combining legacy and dual-screening
 */
export const $getProjectScreeningStats = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }) => {
    const { requireUser, requireProjectAccess, db } =
      await import("~/lib/server/helpers.server");
    const currentUser = await requireUser();
    await requireProjectAccess(data.projectId, currentUser.id);

    const citations = await db.query.projectCitations.findMany({
      where: and(
        eq(projectCitations.projectId, data.projectId),
        eq(projectCitations.screeningPhase, data.phase),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
      columns: {
        screeningStatus: true,
        consensusStatus: true,
        finalDecision: true,
      },
    });

    // Calculate comprehensive stats
    const stats = {
      total: citations.length,
      // Legacy status counts
      pending: 0,
      included: 0,
      excluded: 0,
      maybe: 0,
      // Dual-screening status counts
      dualPending: 0,
      awaitingSecond: 0,
      agreedInclude: 0,
      agreedExclude: 0,
      agreedMaybe: 0,
      conflict: 0,
      adjudicated: 0,
    };

    for (const c of citations) {
      // Legacy counts
      switch (c.screeningStatus) {
        case "pending":
          stats.pending++;
          break;
        case "included":
          stats.included++;
          break;
        case "excluded":
          stats.excluded++;
          break;
        case "maybe":
          stats.maybe++;
          break;
      }

      // Dual-screening counts
      switch (c.consensusStatus) {
        case "pending":
          stats.dualPending++;
          break;
        case "awaiting_second":
          stats.awaitingSecond++;
          break;
        case "agreed_include":
          stats.agreedInclude++;
          break;
        case "agreed_exclude":
          stats.agreedExclude++;
          break;
        case "agreed_maybe":
          stats.agreedMaybe++;
          break;
        case "conflict":
          stats.conflict++;
          break;
        case "adjudicated":
          stats.adjudicated++;
          break;
      }
    }

    return stats;
  });
